import React, { useEffect, useState } from "react";
import Intercom from '@intercom/messenger-js-sdk';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { logout } from '../firebase.js'
import { getDatabase, ref, set, push, onValue, get } from "firebase/database";
import { getApp } from "firebase/app";
import { getStorage, ref as storage_ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { useLocalStorage } from 'hooks/use-local-storage'
import { Tracking } from 'components/Tracking';
import { Spinner } from 'components/Icons';
import { SnackbarProvider, enqueueSnackbar, closeSnackbar } from 'notistack';

export const AuthContext = React.createContext();

const functions = getFunctions(getApp());

if (process.env.REACT_APP_ENV === 'local') {
  connectFunctionsEmulator(functions, "localhost", 5001);
  // connectFunctionsEmulator(functions, "127.0.0.1", 5002);
}
export const create_zip = httpsCallable(functions, 'createZip');
export const get_csv_data = httpsCallable(functions, 'getCSV');
export const get_csv_export = httpsCallable(functions, 'getCSVExport');
export const export_items_to_shopify = httpsCallable(functions, 'export_items_to_shopify', { timeout: 120000 });
export const export_items_to_square = httpsCallable(functions, 'export_items_to_square', { timeout: 120000 });
export const validate_shopify_connection = httpsCallable(functions, 'validate_shopify_connection');
export const delete_items = httpsCallable(functions, 'delete_items');
export const restore_items = httpsCallable(functions, 'restore_items');
export const generate_intercom_jwt = httpsCallable(functions, 'generate_intercom_jwt');
export const get_shopify_data = httpsCallable(functions, 'get_shopify_data');

// User management functions
export const add_user = httpsCallable(functions, 'addUser');
export const remove_user = httpsCallable(functions, 'removeUser');
export const send_account_merge_request = httpsCallable(functions, 'sendAccountMergeRequest');
export const email_exists_and_is_trial = httpsCallable(functions, 'emailExistsAndIsTrialInAuth');
export const phone_number_exists_and_is_trial = httpsCallable(functions, 'phoneNumberExistsAndIsTrialInAuth');

export const SHOPIFY_TITLE_SECTION_SEPARATOR = '_|_';
export const SHOPIFY_TITLE_TYPE_SEPARATOR = ':|:';

export const DEFAULT_SHOPIFY_SETTINGS = {
  title: `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}brand${SHOPIFY_TITLE_SECTION_SEPARATOR}prop${SHOPIFY_TITLE_TYPE_SEPARATOR}department${SHOPIFY_TITLE_SECTION_SEPARATOR}prop${SHOPIFY_TITLE_TYPE_SEPARATOR}category${SHOPIFY_TITLE_SECTION_SEPARATOR}prop${SHOPIFY_TITLE_TYPE_SEPARATOR}style_name`,
  tags: ['FAVES', `prop${SHOPIFY_TITLE_TYPE_SEPARATOR}style_number`],
  sales_channels: [],
  styleNumber_as_sku: false,
  brand_as_vendor: true,
  set_compare_at_price: false,
  set_track_quantity: false,
  set_quantity: false,
  location: "Use Default Location",
}

export const force_logout = () => {
  const data = {
    message: 'Your session has expired. Please log in again or contact support@thefavesapp.com for help.',
    variant: 'warning'
  }

  if (!window.force_logout_notification) {
    window.force_logout_notification = enqueueSnackbar(data.message, { 
      SnackbarProps: {
        onClick: () => {
          closeSnackbar(window.force_logout_notification);
        }
      },
      autoHideDuration: 10000,
      variant: 'success',
      anchorOrigin: {
        vertical: 'bottom', 
        horizontal: 'center', 
      },
      ...data 
    })
  }
  logout()
  // Force reload to clear all data
  setTimeout(function() {
    // eslint-disable-next-line no-self-assign
    window.location.href = window.location.origin;
  }, 4000)
}

const DEPARTMENT_MAP = {
  0: 'MEN',
  1: 'WOMEN',
  2: 'BOYS',
  3: 'GIRLS',
  4: 'HOME',
}

let CATEGORY_LOGO_MAP = {
  'MEN': {},
  'WOMEN': {},
  'BOYS': {},
  'GIRLS': {},
  'HOME': {},
}

export const capitalize = (s) => {
  return String(s[0]).toUpperCase() + String(s).slice(1);
}

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null); // Firebase auth user
  const [user, setUser] = useState(null); // /users/<id> from firebase

  const [accountId, setAccountId] = useState(null)
  const [account, setAccount] = useState(null)
  const [accountIntegrations, setAccountIntegrations] = useState(null)
  const [selectedStore, setSelectedStore] = useLocalStorage('selectedStore')

  const [items, setItems] = useState({})
  const [selectedSeasonitems, setSelectedSeasonItems] = useState({})
  const [customCategories, setCustomCategories] = useState({})
  const [exchangeRates, setExchangeRates] = useState(null)
  const [storeCurrency, setStoreCurrency] = useState('USD')
  const [accountBudgets, setAccountBudgets] = useState(null)

  const [colors, setColors] = useState(null)
  const [userColors, setUserColors] = useState(null)

  const [seasons, setSeasons] = useState(null)
  const [selectedSeason, setSelectedSeason] = useLocalStorage('selectedSeason')

  const [brands, setBrands] = useState({})
  const [userCreatedBrands, setUserCreatedBrands] = useState({})
  const [userBrands, setUserBrands] = useState(null)
  const [categories, setCategories] = useState(null)

  const [collections, setCollections] = useState({'all': { displayName: 'Show All' }})
  const [selectedCollection, setSelectedCollection] = useLocalStorage('selectedCollection')

  const auth = getAuth();
  const db = getDatabase();

  
  const notify = (data) => {
    const notification = enqueueSnackbar(data.message, { 
      SnackbarProps: {
        onClick: () => {
          closeSnackbar(notification);
        }
      },
      autoHideDuration: 5000,
      variant: 'success',
      anchorOrigin: {
        vertical: 'bottom', 
        horizontal: 'center', 
      },
      ...data })
  }

  const addCustomCollection = (collection, callback) => { 
    let new_colleciton = push(ref(db, 'accountCollections/' + selectedStore + '/' + selectedSeason))
    set(new_colleciton, collection).then(callback).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const deleteCustomCollection = (collection, callback) => { 
    items[selectedSeason] && Object.keys(items[selectedSeason]).filter(e => items[selectedSeason][e].collectionId === collection).map(e => {
      updateItem({ ...items[selectedSeason][e], id: e }, { collectionId: null })
    })
    let collection_ref = ref(db, 'accountCollections/' + selectedStore + '/' + selectedSeason + '/' + collection)
    set(collection_ref, null).then(callback).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })

  }

  const sortCustomCollection = (current_collections, collection, old_index, new_index, callback = () => {}) => {
    arraymove(current_collections, old_index, new_index);
    const new_collections = {}
    Object.keys(collections).filter(e => ['all', '-'].indexOf(e) === -1).map(c => {
      new_collections[c] = {
        ...collections[c],
        displayOrder: (current_collections.indexOf(c)+1) * 10
      }
    })
    const collections_ref = ref(db, 'accountCollections/' + selectedStore + '/' + selectedSeason);
    set(collections_ref, new_collections);
  }

  const renameCollection = (collection, title, callback = () => {}) => {
    let category_ref = ref(db, 'accountCollections/' + selectedStore + '/' + selectedSeason + '/' + collection.id)
    delete collection.id
    collection.displayName = title
    set(category_ref, collection).then(() => {
      callback()
    })
  }

  const addCustomCategory = (department, category, callback = () => {}) => {
    let new_category = push(ref(db, `customCategories/${accountId}/${selectedStore}/${department}`))
    let sections_ref = ref(db, `customCategories/${accountId}/${selectedStore}/${department}/sections`)
    set(new_category, category).then(() => {
      get(sections_ref).then((snapshot) => {
        const data = snapshot.val();
        if (!data) {
          let newSections = [{
            categories: [new_category.key]
          }]
          set(sections_ref, newSections)
        } else {
          data[0].categories.push(new_category.key)
          set(sections_ref, data)
        }
      });

      callback()
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const renameCustomCategory = (department, category, title, callback = () => {}) => {
    let category_ref = ref(db, `customCategories/${accountId}/${selectedStore}/${department}/${category}`)
    set(category_ref, {displayName: title}).then(() => {
      callback()
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const deleteCustomCategory = (department, category, callback = () => {}) => {
    const category_ref = ref(db, `customCategories/${accountId}/${selectedStore}/${department}/${category}`)
    const category_id = category_ref.key
    let sections_ref = ref(db, `customCategories/${accountId}/${selectedStore}/${department}/sections`)
    // Remove sections first
    get(sections_ref).then((snapshot) => {
      const data = snapshot.val();
      if (data) {
        data[0].categories = data[0].categories.filter((e) => e !== category_id)
        set(sections_ref, data)
      }
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
    set(category_ref, null).then(callback).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const arraymove = (arr, fromIndex, toIndex) => {
    var element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
  }

  const sortCustomCategory = (department, category, old_index, new_index, callback = () => {}) => {
    let sections_ref = ref(db, `customCategories/${accountId}/${selectedStore}/${department}/sections`)
    get(sections_ref).then(snapshot => {
      const data = snapshot.val();
      arraymove(data[0].categories, old_index, new_index)
      set(sections_ref, data)
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const updateBudget = (store, season_or_collection, category_group, category, value, callback = () => {}) => {
    const budget = ref(db, `accountCategorySectionBudgets/${store}/${season_or_collection}/${category_group}/${category}`)
    const new_budget = {
      budget: value,
      departmentId: category_group
    }

    set(budget, new_budget).then(() => {
      callback()
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const updateStoreName = (store, name, callback = () => {}) => {
    const store_name = ref(db, `accounts/${accountId}/acctStores/${store}/displayName`)

    set(store_name, name).then(() => {
      callback()
    })
  }

  const updateSeasonCollectionBudget = (season_or_collection, value, callback = () => {}) => {
    const budget = ref(db, `accounts/${accountId}/acctStores/${selectedStore}/collectionBudgets/${season_or_collection}`)
    const new_budget = {
      budget: value,
      currency: storeCurrency
    }

    set(budget, new_budget).then(() => {
      callback()
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const getBlob = (url) => {
    return new Promise((resolve, reject) => {
      var xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.onload = function(event){
        var blob = xhr.response;
        resolve(blob);
      };
      xhr.onerror = (e) => {
        console.log(e)
        reject();
      }
      xhr.open('GET', url);
      xhr.send();
    })
  }

  const uploadMissingImage = async () => {
    try {
      const storage = getStorage();
      // const missing_image_ref = storage_ref(storage, `/missing_image.jpg`);
      // const downloadURL = await getDownloadURL(missing_image_ref);
      // const response = await fetch('/missing_image.jpg')
      // const fileData = response.blob()
      const fileData = await getBlob('/missing_image.jpg')
      const destinationRef = storage_ref(storage, `${accountId}/missing_image.jpg`);
      await uploadBytes(destinationRef, fileData);
    } catch(e) {
      console.log('err', e)
    }

    return 'missing_image';
  }

  const sortByDisplayOrder = (a, b) => {
    if (a.displayOrder < b.displayOrder) {
      return -1;
    } else if (a.displayOrder > b.displayOrder) {
      return 1;
    } else {
      return 0;
    }
  }

  const getItem = (product_id, callback) => {
    if (product_id) {
      let product_ref = ref(db, `items/${selectedStore}/${selectedSeason}/${product_id}`)
      get(product_ref).then(snapshot => {
        const data = snapshot.val();
        callback(data)
      }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
    }
  }

  const updateItem = (original_product, values, callback) => {
    const product = {
      ...original_product,
      ...values
    }

    if (original_product.create) {
      // original_product.id = push(ref(db, `items/${selectedStore}/${selectedSeason}`)).key
      product.creationDate = parseInt(new Date().getTime() / 1000)
      product.creatorUserId = currentUser.uid
      product.version = 'web'
    } else {
      product.modificationDate = parseInt(new Date().getTime() / 1000)
      product.lastModifiedByUserId = currentUser.uid
    }

    console.log('product', product)

    // Handle commited/received logic
    // If order status is 0, deliveries cannot exist
    if (product.orderStatus === 0) { product.deliveries = null; }
    // If previous deliveries were not received, and current value is set to receive, we turned it on, so override the above and set the value on
    if (original_product.deliveries === 0 && product.deliveries) { product.deliveries = 1 }
    // If deliveries already exist, set to existing value to avoid rewriting delivery dates
    if (original_product.deliveries !== 0 && product.orderStatus && product.deliveries && original_product.deliveries) { 
      product.deliveries = original_product.deliveries; 
    }
    // If deliveries exist, order status has to be commited / 1
    if (product.deliveries) { product.orderStatus = 1; }
  
    // Remove undefined values
    Object.keys(product).forEach((key) => {
      if (product[key] !== 0 && product[key] !== null && !product[key]) {
        delete product[key]
      }
    })
  
    delete product.id
    delete product.create
    if (!product.brand) { product.brand = 'NoBrand' }
    if (!product.priceInfo) { product.priceInfo = {} }
    if (!product.priceInfo?.currency) { product.priceInfo.currency = storeCurrency }
    if (!product.priceInfo?.retailPriceCurrency) { product.priceInfo.retailPriceCurrency = storeCurrency }

    if (!product.photos || product.photos.length === 0) {
      uploadMissingImage()
      product.photos = ['missing_image']
    }
  
    set(ref(db, `items/${selectedStore}/${selectedSeason}/${original_product.id}`), product).then(() => {
      let deliveries_data = product.deliveries
      let valid_delivery_date = product.deliveries?.[Object.keys(product.deliveries || {})?.[0]]?.deliveryDate

      if (deliveries_data === 0 || deliveries_data === 1 || !valid_delivery_date) {
        set(ref(db, `items/${selectedStore}/${selectedSeason}/${original_product.id}/deliveries`), null)
        deliveries_data = {
          deliveryDate: parseInt(Date.now() / 1000),
          quantity: product.priceInfo?.quantity || 0
        }
        if (product.orderStatus && product.deliveries) {
          push(ref(db, `items/${selectedStore}/${selectedSeason}/${original_product.id}/deliveries`), deliveries_data)
        }
      } 
      
      if (callback) {
        callback();
      }
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const deleteItem = (product, callback) => {
    set(ref(db, `items/${selectedStore}/${selectedSeason}/${product.id}`), null).then(() => {
      if (callback) {
        callback();
      }
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const deleteShopifyIntegration = (store, callback) => {
    set(ref(db, `integrations/${Object.keys(account.acctStores)[0]}/shopify_v2/${store}`), null).then(() => {
      if (callback) {
        callback();
      }
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const adjustItemPrice = (product, store) => {
    console.log('adjustItemPrice', product)
    if (storeCurrency !== account.acctStores[store].currency) {
      if (product.priceInfo) {
        product.priceInfo.retailPriceCurrency = account.acctStores[store].currency
        if (product?.priceInfo?.retailPrice) {
          product.priceInfo.retailPrice = product.priceInfo.retailPrice * exchangeRates[account.acctStores[store].currency].rate / exchangeRates[storeCurrency].rate
        }
      }
    }

    return product
  }

  /*
  Helper function to correctly migrate category when copying/moving products across stores
  */
  const adjustItemCategory = (product, store) => {
    if (!product.categoryId) {
      return product
    }

    let category_in_new_store = false;
    onValue(
      // Get "new" store categories and try to find a matching category for the product
      ref(db, `customCategories/${accountId}/${store}`), 
      (snapshot) => {
        const data = snapshot.val();
        let result = {}

        if (data) {
          Object.keys(data).forEach((category_group_id) => {
            if (parseInt(category_group_id) === product.categoryType) {
              Object.keys(data[category_group_id]).forEach((category_id) => {
                if (customCategories?.[product.categoryId]?.category?.toLowerCase() ===  data?.[category_group_id]?.[category_id]?.['displayName']?.toLowerCase()) {
                  category_in_new_store = category_id
                }
              });
            }
          });
        }
      }
    );

    if (category_in_new_store) {
      product.categoryId = category_in_new_store
    } else {
      // Category does not exist, so we create it here
      let department = product.categoryType
      let category = {
        displayName: customCategories?.[product.categoryId]?.category
      }
      let new_category = push(ref(db, `customCategories/${accountId}/${store}/${department}`))
      let sections_ref = ref(db, `customCategories/${accountId}/${store}/${department}/sections`)
      set(new_category, category).then(() => {
        get(sections_ref).then((snapshot) => {
          const data = snapshot.val();
          if (!data) {
            let newSections = [{
              categories: [new_category.key]
            }]
            set(sections_ref, newSections)
          } else {
            data[0].categories.push(new_category.key)
            set(sections_ref, data)
          }
        });
      }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
      product.categoryId = new_category.key
    }
    return product
  }

  const copyItem = (product, new_path, callback) => {
    product = adjustItemPrice(product, new_path.store)
    product = adjustItemCategory(product, new_path.store)

    const new_product = push(ref(db, `items/${new_path.store}/${new_path.season}`))
    delete product.id
    delete product?.integrations
    product.copied_from = {
      seasonId: selectedSeason,
      storeId: selectedStore,
      timestamp: parseInt(new Date().getTime() / 1000)
    }
    set(new_product, product).then(() => {
      if (callback) {
        callback();
      }
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const moveItem = (product, new_path, callback) => {
    product = adjustItemPrice(product, new_path.store)
    product = adjustItemCategory(product, new_path.store)
    const old_product = ref(db, `items/${selectedStore}/${selectedSeason}/${product.id}`)
    const new_product = ref(db, `items/${new_path.store}/${new_path.season}/${product.id}`)

    delete product.id
    product.moved_from = {
      seasonId: selectedSeason,
      storeId: selectedStore,
      timestamp: parseInt(new Date().getTime() / 1000)
    }

    set(new_product, product).then(() => {
      if (selectedStore === new_path.store && selectedSeason === new_path.season) {
        // If same store/season selected, don't delete the item
        callback();
        return
      } else {
        set(old_product, null).then(() => {
          if (callback) {
            callback();
          }
        })
      }
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const updateShopifyIntegration = (integration, settings, callback) => {
    const integrations_ref = ref(db, `integrations/${Object.keys(account.acctStores)[0]}/shopify_v2/${integration}/settings`)
    console.log('settings', settings)
    set(integrations_ref, settings).then(() => {
      if (callback) {
        callback()
      }
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const updateSquareIntegration = (settings, callback) => {
    const integrations_ref = ref(db, `integrations/${Object.keys(account.acctStores)[0]}/square`)
    set(integrations_ref, settings).then(() => {
      if (callback) {
        callback()
      }
    }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
  }

  const printStyles = (itemIds, config, callback) => {
    const functions = getFunctions();
    const addMessage = httpsCallable(functions, 'printStyles');
    addMessage({ 
      storeId: selectedStore,
      seasonId: selectedSeason,
      itemIds: itemIds,
      ...config
    }).then((result) => {
      const data = result.data;
      const sanitizedMessage = data.text;
      if (callback) {
        callback(data)
      }
      // console.log('data', data)
      // console.log('sanitizedMessage', sanitizedMessage)
    }).catch((error) => {
      const code = error.code;
      const message = error.message;
      const details = error.details;
      // console.log('code', code)
      // console.log('message', message)
      // console.log('details', details)
      if (callback) {
        callback(error)
      }
    });
  }

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        const user_ref = ref(db, 'users/' + user.uid);
        get(user_ref).then((snapshot) => {
          let user_account = snapshot.val()?.userAccount
          if (user_account) {
            const account_ref = ref(db, 'accounts/' + user_account);
            get(account_ref).then((snapshot) => {
              const data = snapshot.val();
              if (data.features && data.features.webPortal) {
                Tracking.identify(user.uid)
                Tracking.people.set({ "Email": user.email });
                setCurrentUser(user);
              }
            })
          }
        }).catch((err) => { if (err.message == 'Permission denied') { force_logout() } })
      }
    });
  }, [auth]);

  useEffect(() => {
    if (currentUser && accountId === -1) {
      logout();
    }
  }, [currentUser, accountId]);

  // When we get the user, fetch it from firebase and save account id
  useEffect(() => {
    if (!currentUser) return
    const user_ref = ref(db, 'users/' + currentUser.uid);
    const brands_ref = ref(db, 'brands');
    const colors_ref = ref(db, 'colors');
    const seasons_ref = ref(db, 'seasons');
    const exchange_rates_ref = ref(db, 'exchangeRates');
    const categories_ref = ref(db, 'categories/134');

    onValue(user_ref, (snapshot) => { 
      let user = snapshot.val()
      setUser(user)
      console.log('snapshot.val()?.userAccount || -1', snapshot.val()?.userAccount || -1)
      console.log('currentUser.uid', currentUser.uid)
      setAccountId(snapshot.val()?.userAccount || -1);

      generate_intercom_jwt({
        payload: {
          user_id: currentUser.uid,
          name: user.userFirstName,
          email: user.userEmail,
        }
      }).then((result) => {
        Intercom({
          app_id: 'bdhqfqak',
          intercom_user_jwt: result.data
        });
      })
    });
    onValue(brands_ref, (snapshot) => { setBrands(snapshot.val()); });
    onValue(exchange_rates_ref, (snapshot) => { setExchangeRates(snapshot.val()); });
    onValue(seasons_ref, (snapshot) => { setSeasons(snapshot.val()); });
    onValue(colors_ref, (snapshot) => { 
      // Add color id for easier CRUD operations later
      let raw_colors = snapshot.val();
      Object.keys(raw_colors).forEach((c) => {
        raw_colors[c].id = c
      })
      setColors(raw_colors);
    });
    onValue(categories_ref, (snapshot) => {
      let raw_data = snapshot.val();

      raw_data = raw_data.map((categories, department) => {
        const { sections } = categories
        let sections_map = {}
        Object.keys(sections).forEach((section) => {
          sections[section].categories.forEach((section_category) => {
            sections_map[section_category] = sections[section].sectionName
          })
        })

        let normalised_categories = {}
        Object.keys(categories).forEach((cat_id) => {
          CATEGORY_LOGO_MAP[DEPARTMENT_MAP[department]][categories[cat_id].displayName] = categories[cat_id].logo

          normalised_categories[cat_id] = {
            ...categories[cat_id],
            section: sections_map[cat_id]
          }
        })

        return {
          id: department,
          department: DEPARTMENT_MAP[department],
          options: normalised_categories
        }
      })
      
      setCategories(raw_data);
    });
  }, [db, currentUser])


  useEffect(() => {
    if (accountId === -1) {
      console.warn("User account doesn't exist, show warning?")
    }

    if (!accountId) return
    if (!selectedStore || selectedStore === -1) {
      setSelectedStore(accountId)
      return
    }
    const account_ref = ref(db, 'accounts/' + accountId);
    const user_brands_ref = ref(db, 'userCreatedBrands/' + accountId);
    
    onValue(account_ref, (snapshot) => {
      const data = snapshot.val();
      setAccount(data);
      if (!data.acctStores.hasOwnProperty(selectedStore)) {
        setSelectedStore(accountId)
      } else {
        setStoreCurrency(data.acctStores[selectedStore].currency);
      }
    });

    onValue(user_brands_ref, (snapshot) => {
      const data = snapshot.val();
      setUserCreatedBrands(data || {});
    });
  }, [db, accountId, selectedStore])

  useEffect(() => {
    const items_ref = ref(db, 'items/' + selectedStore);

    return onValue(items_ref, (snapshot) => {
      const data = snapshot.val()
      setItems(data)
    });
  }, [db, selectedStore])

  useEffect(() => {
    if (!selectedStore || !selectedSeason) return
    const items_ref = ref(db, `items/${selectedStore}/${selectedSeason}`);

    return onValue(items_ref, (snapshot) => {
      const data = snapshot.val()
      setSelectedSeasonItems(data)
    });

  }, [db, selectedStore, selectedSeason])

  useEffect(() => {
    if (!account || !colors) return
    if (account.features && !account.features.webPortal) {
      force_logout()
    } else {
      // console.log("true account.features", account, accountId)
    }

    const user_colors_ref = ref(db, 'userColors/' + account.acctOwner);

    onValue(user_colors_ref, (snapshot) => {
      const data = snapshot.val().map((color) => {
        return colors[color]
      })
      setUserColors(data);
    });

    const integrations_ref = ref(db, `integrations/${Object.keys(account.acctStores)[0]}`); 
    onValue(integrations_ref, (snapshot) => {
      const data = snapshot.val();
      setAccountIntegrations(data);
    })
  }, [db, account, colors])

  useEffect(() => {
    if (!account) return
    const custom_categories_ref = ref(db, `customCategories/${accountId}/${selectedStore}`);

    onValue(custom_categories_ref, (snapshot) => {
      const data = snapshot.val();
      let result = {}

      if (data) {
        Object.keys(data).forEach((category_group_id) => {
          Object.keys(data[category_group_id]).forEach((category_id) => {
            result[category_id] = {
              category_group_id,
              category_id: category_id,
              category_group: DEPARTMENT_MAP[category_group_id],
              category: data[category_group_id][category_id]['displayName'],
              sorting: (data[category_group_id].sections && data[category_group_id].sections[0]?.categories) || []
            }
          });
        });
        setCustomCategories(result);
      } else {
        setCustomCategories([])
      }

    });
  }, [db, account, selectedStore])

  useEffect(() => {
    if (!brands || !userCreatedBrands || !Object.keys(brands).length || !Object.keys(userCreatedBrands).length) return
    const user_brands_ref = ref(db, 'userBrands/' + currentUser.uid);

    onValue(user_brands_ref, (snapshot) => {
      const data = snapshot.val() || [];

      // Fixes weird crash when data here becomes an object instead of an array when someone opens IOS app
      if (Array.isArray(data)) {
        setUserBrands(data?.map((e) => ({
          value: e,
          text: (brands?.[e] && brands?.[e]?.displayName) || (userCreatedBrands?.[e] && userCreatedBrands?.[e]?.displayName) || "-"
        })));
      }
    });
  }, [db, brands, userCreatedBrands, currentUser])

  useEffect(() => {
    if (selectedStore && selectedSeason) {
      const collections_ref = ref(db, 'accountCollections/' + selectedStore + '/' + selectedSeason);
      const account_budgets_ref = ref(db, 'accountCategorySectionBudgets/' + selectedStore);

      onValue(collections_ref, (snapshot) => {
        const data = snapshot.val() || [];
        setCollections({
          'all': { displayName: 'Show All' },
          ...data,
          '-': { displayName: 'Not in a Collection' },
        })
      })

      onValue(account_budgets_ref, (snapshot) => {
        const data = snapshot.val() || {};
        setAccountBudgets(data);
      })
    } else {
      setCollections({'all': { displayName: 'Show All' }})
    }
  }, [db, selectedStore, selectedSeason])

  useEffect(() => {
    if (!collections.hasOwnProperty(selectedCollection)) {
      setSelectedCollection('all')
    }
  }, [selectedCollection, setSelectedCollection, collections])

  // ALl data loaded, render the customer portal
  useEffect(() => {
    // console.log('account', account)
    // console.log('currentUser', currentUser)
    // console.log('accountId', accountId)
    // console.log('items', items)
    // console.log('seasons', seasons)
    // console.log('userBrands', userBrands)
    if (!account || !currentUser || !accountId || !seasons) return

    // if selected season not set, pick the last one
    if (!selectedSeason || !seasons.hasOwnProperty(selectedSeason)) {
      if (seasons) {
        setSelectedSeason(Object.keys(seasons).sort((a, b) => seasons[a].displayOrder > seasons[b].displayOrder ? 1 : -1).reverse()[0])
      }
    }

    setLoading(false);
  }, [account, currentUser, accountId, items, seasons, userBrands, selectedSeason, setSelectedSeason])

  if (loading) {
    return <Spinner/>
  }
  
  return (
    <AuthContext.Provider 
      value={{ 
        user,
        currentUser, 
        account,
        accountId, 
        items,
        selectedSeasonitems,
        exchangeRates,
        storeCurrency,
        accountBudgets,
        seasons, 
        brands,
        customCategories, 
        userColors,
        colors,
        userCreatedBrands,
        selectedSeason, 
        setSelectedSeason,
        userBrands,
        selectedStore,
        setSelectedStore,
        CATEGORY_LOGO_MAP,
        categories,
        updateItem,
        deleteItem,
        copyItem,
        moveItem,
        getItem,
        addCustomCategory,
        renameCustomCategory,
        deleteCustomCategory,
        sortCustomCategory,
        addCustomCollection,
        deleteCustomCollection,
        sortCustomCollection,
        renameCollection,
        updateBudget,
        updateSeasonCollectionBudget,
        updateShopifyIntegration,
        printStyles,
        collections,
        selectedCollection,
        setSelectedCollection,
        accountIntegrations,
        sortByDisplayOrder,
        uploadMissingImage,
        updateSquareIntegration,
        deleteShopifyIntegration,
        notify,
        updateStoreName,
        database: db,
        DEFAULT_SHOPIFY_SETTINGS
      }}
    >
      <SnackbarProvider/>
      {children}
    </AuthContext.Provider>
  );
};
